import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../../contexts/LanguageContext';
import { CalendarIcon, PlayIcon, CheckCircleIcon, XCircleIcon, ArrowLeftIcon } from 'lucide-react';
import api from '../../services/api';
import { authService } from '../../services/authService';
import toast from 'react-hot-toast';
import { getCountryTranslation } from '../../services/countriesTranslationsService';
import { getRegionTranslation } from '../../services/regionsTranslationsService';
import { getAcademyTranslation } from '../../services/academiesTranslationsService';
import { getDistrictTranslation } from '../../services/districtsTranslationsService';
import { getInstitutionTranslation } from '../../services/institutionsTranslationsService';
import { getDepartmentTranslation } from '../../services/departmentsTranslationsService';
import { useStudentFocusSession } from '../../contexts/StudentFocusSessionContext';
import FullscreenNumberDisplay from '../../components/student/FullscreenNumberDisplay';
import DailyExercisesHero from '../../components/student/dailyExercises/DailyExercisesHero';
import DailyExercisesStatsRankingSection from '../../components/student/dailyExercises/DailyExercisesStatsRankingSection';

// استخراج معرفات الهيكل من organizationalAccess (نفس هيكل الطالب المستخدم في التقارير)
const normalizeOrgScope = (access) => {
  if (!access || typeof access !== 'object') return null;
  const id = (key) => access[key] ?? access[key.replace(/([A-Z])/g, '_$1').toLowerCase().replace(/^_/, '')];
  const departmentId = id('departmentId') ?? id('department_id');
  const countryId = id('countryId') ?? id('country_id');
  const regionId = id('regionId') ?? id('region_id');
  const academyId = id('academyId') ?? id('academy_id');
  const districtId = id('districtId') ?? id('district_id');
  const institutionId = id('institutionId') ?? id('institution_id');
  return {
    countryId: countryId != null && countryId !== '' ? String(countryId) : '',
    regionId: regionId != null && regionId !== '' ? String(regionId) : '',
    academyId: academyId != null && academyId !== '' ? String(academyId) : '',
    districtId: districtId != null && districtId !== '' ? String(districtId) : '',
    institutionId: institutionId != null && institutionId !== '' ? String(institutionId) : '',
    departmentId: departmentId != null && departmentId !== '' ? String(departmentId) : ''
  };
};

const normalizeOrgScopeNames = (access) => {
  if (!access || typeof access !== 'object') return null;
  const n = (camel, snake) => access[camel] ?? access[snake] ?? '';
  return {
    countryName: n('countryName', 'country_name'),
    regionName: n('regionName', 'region_name'),
    academyName: n('academyName', 'academy_name'),
    districtName: n('districtName', 'district_name'),
    institutionName: n('institutionName', 'institution_name'),
    departmentName: n('departmentName', 'department_name')
  };
};

const DailyExercises = () => {
  const { t } = useTranslation();
  const { isRTL, currentLanguage } = useLanguage();

  // دالة لتحويل الأرقام إلى الأرقام اللاتينية في اللغة العربية
  const formatNumber = (number, decimals = 0) => {
    if (isRTL) {
      // استخدام الأرقام اللاتينية حتى في اللغة العربية
      return new Intl.NumberFormat('ar-SA', {
        numberingSystem: 'latn',
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals
      }).format(number);
    }
    return decimals > 0 ? number.toFixed(decimals) : number.toString();
  };

  // دالة لتنسيق التاريخ مع الأرقام اللاتينية
  const formatDate = (date) => {
    const formatted = date.toLocaleDateString(isRTL ? 'ar-SA' : 'en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      numberingSystem: isRTL ? 'latn' : undefined
    });
    return formatted;
  };

  // حالة التمرين المتاح
  const [availableExercise, setAvailableExercise] = useState(null);
  const [loadingExercise, setLoadingExercise] = useState(false);
  const [exerciseData, setExerciseData] = useState(null);
  const [attemptId, setAttemptId] = useState(null);

  // حالة شاشة اللعب
  const [gameState, setGameState] = useState('checking'); // 'checking', 'noExercise', 'ready', 'countdown', 'playing', 'answer', 'roundComplete', 'finalResult'
  const [trafficLightIndex, setTrafficLightIndex] = useState(0); // 0=بداية، 1=أصفر، 2=برتقالي، 3=أخضر
  const [currentRound, setCurrentRound] = useState(0);
  const [currentRoundData, setCurrentRoundData] = useState(null);
  const [currentNumberIndex, setCurrentNumberIndex] = useState(0);
  const [studentAnswer, setStudentAnswer] = useState('');
  const [roundStartTime, setRoundStartTime] = useState(null);
  const [roundAnswers, setRoundAnswers] = useState([]); // [{roundNumber, studentAnswer, totalTimeMs}]
  const [finalResult, setFinalResult] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  // حالة الإحصائيات
  const [exerciseStats, setExerciseStats] = useState(null);
  const [loadingStats, setLoadingStats] = useState(false);

  // تاب الإحصائيات / ترتيب الطلاب وقسم الطالب للترتيب
  const [activeTab, setActiveTab] = useState('departmentRanking');
  const [departmentLeaderboard, setDepartmentLeaderboard] = useState([]);
  const [loadingDepartmentLeaderboard, setLoadingDepartmentLeaderboard] = useState(false);
  const [studentDepartmentId, setStudentDepartmentId] = useState(null);
  // فلتر عرض النتائج بين تاريخين (ترتيب القسم)
  const [rankingDateFrom, setRankingDateFrom] = useState('');
  const [rankingDateTo, setRankingDateTo] = useState('');
  const [appliedRankingDateFrom, setAppliedRankingDateFrom] = useState('');
  const [appliedRankingDateTo, setAppliedRankingDateTo] = useState('');
  const [showRankingFilter, setShowRankingFilter] = useState(false);

  // استراحة بعد كل 10 جولات: عد تنازلي 6 ثوانٍ + زر خذ استراحة / استكمال
  const [breakCountdown, setBreakCountdown] = useState(null);
  const [breakPaused, setBreakPaused] = useState(false);
  const pendingNextRoundRef = useRef(null);
  const breakPausedRef = useRef(false);
  const breakCountdownIntervalRef = useRef(null);

  // Refs للتحكم في الأصوات والتوقيت
  const audioContextRef = useRef(null);
  const intervalRef = useRef(null);
  const roundTimeoutRef = useRef(null);
  const trafficLightTimeoutsRef = useRef([]);

  const { setFocusSession } = useStudentFocusSession();
  /** لا نفعّل وضع التركيز في ready/checking/noExercise — تبقى الخلفية والهيدر ظاهرة كباقي صفحات الطالب */
  useEffect(() => {
    const immersive = ['countdown', 'playing', 'answer', 'break', 'roundComplete', 'finalResult'].includes(
      gameState
    );
    setFocusSession(immersive);
    return () => setFocusSession(false);
  }, [gameState, setFocusSession]);

  // إنشاء صوت التنبيه
  const playBeepSound = () => {
    try {
      if (!audioContextRef.current || audioContextRef.current.state === 'closed') {
        audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      }
      
      const oscillator = audioContextRef.current.createOscillator();
      const gainNode = audioContextRef.current.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContextRef.current.destination);
      
      oscillator.frequency.setValueAtTime(800, audioContextRef.current.currentTime);
      oscillator.type = 'sine';
      
      gainNode.gain.setValueAtTime(0.3, audioContextRef.current.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContextRef.current.currentTime + 0.1);
      
      oscillator.start(audioContextRef.current.currentTime);
      oscillator.stop(audioContextRef.current.currentTime + 0.1);
    } catch (error) {
      console.log('Audio not supported');
    }
  };

  // صوت التصفيق للإجابة الصحيحة
  const playSuccessSound = () => {
    try {
      if (!audioContextRef.current || audioContextRef.current.state === 'closed') {
        audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      }
      
      const clapCount = 6;
      const clapInterval = 0.15;
      
      for (let i = 0; i < clapCount; i++) {
        const oscillator = audioContextRef.current.createOscillator();
        const gainNode = audioContextRef.current.createGain();
        
        oscillator.connect(gainNode);
        gainNode.connect(audioContextRef.current.destination);
        
        oscillator.frequency.setValueAtTime(2000 + Math.random() * 1000, audioContextRef.current.currentTime + i * clapInterval);
        oscillator.type = 'square';
        
        gainNode.gain.setValueAtTime(0, audioContextRef.current.currentTime + i * clapInterval);
        gainNode.gain.linearRampToValueAtTime(0.3, audioContextRef.current.currentTime + i * clapInterval + 0.02);
        gainNode.gain.exponentialRampToValueAtTime(0.01, audioContextRef.current.currentTime + i * clapInterval + 0.1);
        
        oscillator.start(audioContextRef.current.currentTime + i * clapInterval);
        oscillator.stop(audioContextRef.current.currentTime + i * clapInterval + 0.1);
      }
    } catch (error) {
      console.log('Audio not supported');
    }
  };

  // صوت نقرة لإشارة المرور
  const playClickSound = () => {
    try {
      if (!audioContextRef.current || audioContextRef.current.state === 'closed') {
        audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      }
      const oscillator = audioContextRef.current.createOscillator();
      const gainNode = audioContextRef.current.createGain();
      oscillator.connect(gainNode);
      gainNode.connect(audioContextRef.current.destination);
      oscillator.frequency.setValueAtTime(1200, audioContextRef.current.currentTime);
      oscillator.type = 'sine';
      gainNode.gain.setValueAtTime(0.2, audioContextRef.current.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContextRef.current.currentTime + 0.08);
      oscillator.start(audioContextRef.current.currentTime);
      oscillator.stop(audioContextRef.current.currentTime + 0.08);
    } catch (error) {
      console.log('Audio not supported');
    }
  };

  // صوت "Ohhhhh" للإجابة الخاطئة
  const playEncouragementSound = () => {
    try {
      if (!audioContextRef.current || audioContextRef.current.state === 'closed') {
        audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      }
      
      const oscillator = audioContextRef.current.createOscillator();
      const gainNode = audioContextRef.current.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContextRef.current.destination);
      
      oscillator.frequency.setValueAtTime(150, audioContextRef.current.currentTime);
      oscillator.frequency.setValueAtTime(120, audioContextRef.current.currentTime + 0.3);
      oscillator.frequency.setValueAtTime(100, audioContextRef.current.currentTime + 0.6);
      oscillator.type = 'sawtooth';
      
      gainNode.gain.setValueAtTime(0, audioContextRef.current.currentTime);
      gainNode.gain.linearRampToValueAtTime(0.2, audioContextRef.current.currentTime + 0.1);
      gainNode.gain.linearRampToValueAtTime(0.15, audioContextRef.current.currentTime + 0.4);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContextRef.current.currentTime + 1.2);
      
      oscillator.start(audioContextRef.current.currentTime);
      oscillator.stop(audioContextRef.current.currentTime + 1.2);
    } catch (error) {
      console.log('Audio not supported');
    }
  };

  // التحقق من وجود تمرين متاح
  const checkAvailableExercise = async () => {
    setLoadingExercise(true);
    try {
      const response = await api.get('/exercises/available');
      
      if (response.data.success && response.data.data.exercises.length > 0) {
        const exercise = response.data.data.exercises[0];
        setAvailableExercise(exercise);
        setGameState('ready');
        // جلب الإحصائيات عند وجود تمرين
        fetchExerciseStats(exercise.exerciseId);
      } else {
        setAvailableExercise(null);
        setGameState('noExercise');
      }
    } catch (error) {
      console.error('Error checking available exercise:', error);
      toast.error(error.response?.data?.message || t('dailyExercises.errorCheckingExercise'));
      setAvailableExercise(null);
      setGameState('noExercise');
    } finally {
      setLoadingExercise(false);
    }
  };

  // بدء التمرين
  const startExercise = async () => {
    if (!availableExercise) return;

    setIsLoading(true);
    try {
      const response = await api.post(`/exercises/${availableExercise.exerciseId}/start`);
      
      if (response.data.success) {
        const data = response.data.data;
        setExerciseData(data);
        setAttemptId(data.attemptId);
        setCurrentRound(0);
        setRoundAnswers([]);
        
        // بدء الجولة الأولى
        startRound(0, data.rounds[0]);
      }
    } catch (error) {
      console.error('Error starting exercise:', error);
      toast.error(error.response?.data?.message || t('dailyExercises.errorStartingExercise'));
    } finally {
      setIsLoading(false);
    }
  };

  // بدء عرض الأرقام (يُستدعى بعد إشارة المرور أو مباشرة للجولات التالية)
  const startNumberDisplay = (roundData) => {
    if (!roundData?.flashAnzanSession?.numbers) return;
    setRoundStartTime(Date.now());
    const flashAnzanSession = roundData.flashAnzanSession;
    let index = 0;
    intervalRef.current = setInterval(() => {
      if (index < flashAnzanSession.numbers.length) {
        setCurrentNumberIndex(index);
        playBeepSound();
        index++;
      } else {
        clearInterval(intervalRef.current);
        setGameState('answer');
      }
    }, flashAnzanSession.displayTime);
  };

  // بدء جولة
  const startRound = (roundIndex, roundData) => {
    if (!roundData) return;

    setCurrentRound(roundIndex);
    setCurrentRoundData(roundData);
    setCurrentNumberIndex(0);
    setStudentAnswer('');

    // الجولة الأولى فقط: إشارة مرور (أصفر → برتقالي → أخضر) قبل ظهور أول رقم
    if (roundIndex === 0) {
      setTrafficLightIndex(0);
      setGameState('countdown');

      // مسح أي مؤقتات سابقة
      trafficLightTimeoutsRef.current.forEach((t) => clearTimeout(t));
      trafficLightTimeoutsRef.current = [];

      const INTERVAL = 1000;
      const FIRST_DELAY = 1150; // إطالة بسيطة قبل المصباح الثاني لانتظام الإحساس
      const t1 = setTimeout(() => {
        setTrafficLightIndex(1);
        playClickSound();
      }, INTERVAL);
      const t2 = setTimeout(() => {
        setTrafficLightIndex(2);
        playClickSound();
      }, INTERVAL + FIRST_DELAY);
      const t3 = setTimeout(() => {
        setTrafficLightIndex(3);
        playClickSound();
      }, INTERVAL + FIRST_DELAY + INTERVAL);
      const t4 = setTimeout(() => {
        trafficLightTimeoutsRef.current = [];
        setGameState('playing');
        startNumberDisplay(roundData);
      }, INTERVAL + FIRST_DELAY + INTERVAL * 2);

      trafficLightTimeoutsRef.current = [t1, t2, t3, t4];
      return;
    }

    // الجولات التالية: عرض الأرقام مباشرة
    setRoundStartTime(Date.now());
    setGameState('playing');
    startNumberDisplay(roundData);
  };

  // بدء الجولة التالية بعد الاستراحة (من عد تنازلي أو زر استكمال)
  const startNextRoundAfterBreak = () => {
    const nextIndex = pendingNextRoundRef.current;
    if (nextIndex == null || !exerciseData?.rounds?.[nextIndex]) return;
    if (breakCountdownIntervalRef.current) {
      clearInterval(breakCountdownIntervalRef.current);
      breakCountdownIntervalRef.current = null;
    }
    setBreakCountdown(null);
    setBreakPaused(false);
    breakPausedRef.current = false;
    pendingNextRoundRef.current = null;
    setStudentAnswer('');
    setGameState('playing');
    startRound(nextIndex, exerciseData.rounds[nextIndex]);
  };

  // عد تنازلي الاستراحة (6 ثوانٍ) ثم بدء الجولة تلقائياً ما لم يضغط "خذ استراحة"
  useEffect(() => {
    if (gameState !== 'break' || breakPaused || breakCountdown == null) return;
    breakPausedRef.current = false;
    breakCountdownIntervalRef.current = setInterval(() => {
      setBreakCountdown((prev) => {
        if (prev <= 1) {
          if (breakCountdownIntervalRef.current) {
            clearInterval(breakCountdownIntervalRef.current);
            breakCountdownIntervalRef.current = null;
          }
          // تأجيل الانتقال للدورة التالية لتجنب تعليق الشاشة داخل callback الـ setState
          if (!breakPausedRef.current) {
            setTimeout(() => startNextRoundAfterBreak(), 0);
          }
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => {
      if (breakCountdownIntervalRef.current) {
        clearInterval(breakCountdownIntervalRef.current);
        breakCountdownIntervalRef.current = null;
      }
    };
  }, [gameState, breakPaused]);

  // إرسال إجابة الجولة
  const submitRoundAnswer = async () => {
    if (!studentAnswer || !currentRoundData) return;

    const totalTimeMs = Date.now() - roundStartTime;
    const roundNumber = currentRound + 1;

    const answer = {
      roundNumber: roundNumber,
      studentAnswer: parseInt(studentAnswer),
      totalTimeMs: totalTimeMs
    };
    const updatedAnswers = [...roundAnswers, answer];
    setRoundAnswers(updatedAnswers);

    const totalRounds = exerciseData.rounds.length;
    const nextRoundIndex = currentRound + 1;

    if (nextRoundIndex >= totalRounds) {
      await submitAllAnswers(updatedAnswers);
      return;
    }

    // إذا التمرين فيه أقل من 10 جولات: استمرار بالتتابع دون توقف
    if (totalRounds < 10) {
      startRound(nextRoundIndex, exerciseData.rounds[nextRoundIndex]);
      return;
    }

    // بعد كل 10 جولات (10، 20، 30، ...): عرض شاشة استراحة مع عد تنازلي 6 ثوانٍ وزر "خذ استراحة"
    if (nextRoundIndex % 10 === 0) {
      pendingNextRoundRef.current = nextRoundIndex;
      breakPausedRef.current = false;
      setBreakPaused(false);
      setBreakCountdown(6);
      setGameState('break');
      return;
    }

    // بين الجولات (دون حد 10): انطلاق الجولة التالية مباشرة
    startRound(nextRoundIndex, exerciseData.rounds[nextRoundIndex]);
  };

  // إرسال جميع الإجابات
  const submitAllAnswers = async (answers) => {
    if (!attemptId) return;

    setIsLoading(true);
    try {
      const response = await api.post(`/exercises/attempts/${attemptId}/submit-all`, {
        answers: answers
      });

      if (response.data.success) {
        setFinalResult(response.data.data);
        setGameState('finalResult');
        
        // تحديث الإحصائيات بعد إكمال التمرين
        if (availableExercise?.exerciseId) {
          fetchExerciseStats(availableExercise.exerciseId);
        }
        
        // تشغيل الصوت المناسب حسب النتيجة
        const totalScore = response.data.data.totalScore;
        const totalRounds = response.data.data.rounds.length;
        const correctRounds = response.data.data.rounds.filter(r => r.isCorrect).length;
        const accuracy = (correctRounds / totalRounds) * 100;
        
        if (accuracy >= 80) {
          playSuccessSound();
        } else if (accuracy >= 50) {
          playEncouragementSound();
        } else {
          playEncouragementSound();
        }
      }
    } catch (error) {
      console.error('Error submitting answers:', error);
      toast.error(error.response?.data?.message || t('dailyExercises.errorSubmittingAnswers'));
    } finally {
      setIsLoading(false);
    }
  };

  // حل أسماء الهيكل إلى departmentId (نفس منطق التقارير عندما يعيد الـ API أسماء فقط)
  const resolveDepartmentIdFromScopeNames = async (scopeNames) => {
    if (!scopeNames || (!scopeNames.departmentName && !scopeNames.institutionName)) return null;
    const matchName = (item, name, translated) => !name ? false : (item?.name === name || translated === name);
    try {
      const countriesRes = await api.get('/organization/countries?page=1&limit=100').catch(() => ({}));
      const countriesList = countriesRes?.data?.data?.countries || [];
      const country = scopeNames.countryName ? countriesList.find((c) => matchName(c, scopeNames.countryName, getCountryTranslation(c?.name, currentLanguage))) : null;
      if (!country) return null;
      const regionsRes = await api.get(`/organization/countries/${country.id}/regions?page=1&limit=100`).catch(() => ({}));
      const regionsList = regionsRes?.data?.data?.regions || [];
      const region = scopeNames.regionName ? regionsList.find((r) => matchName(r, scopeNames.regionName, getRegionTranslation(r?.name, currentLanguage))) : null;
      if (!region) return null;
      const academiesRes = await api.get(`/organization/regions/${region.id}/academies?page=1&limit=100`).catch(() => ({}));
      const academiesList = academiesRes?.data?.data?.academies || [];
      const academy = scopeNames.academyName ? academiesList.find((a) => matchName(a, scopeNames.academyName, getAcademyTranslation(a?.name, currentLanguage))) : null;
      if (!academy) return null;
      const districtsRes = await api.get(`/organization/academies/${academy.id}/districts?page=1&limit=100`).catch(() => ({}));
      const districtsList = districtsRes?.data?.data?.districts || [];
      const district = scopeNames.districtName ? districtsList.find((d) => matchName(d, scopeNames.districtName, getDistrictTranslation(d?.name, currentLanguage))) : null;
      if (!district) return null;
      const institutionsRes = await api.get(`/organization/districts/${district.id}/institutions?page=1&limit=100`).catch(() => ({}));
      const institutionsList = institutionsRes?.data?.data?.institutions || [];
      const institution = scopeNames.institutionName ? institutionsList.find((i) => matchName(i, scopeNames.institutionName, getInstitutionTranslation(i?.name, currentLanguage))) : null;
      if (!institution) return null;
      const deptsRes = await api.get(`/organization/institutions/${institution.id}/departments?page=1&limit=100`).catch(() => ({}));
      const deptsList = deptsRes?.data?.data?.departments || [];
      const department = scopeNames.departmentName ? deptsList.find((d) => matchName(d, scopeNames.departmentName, getDepartmentTranslation(d?.name, currentLanguage))) : null;
      return department ? String(department.id) : null;
    } catch (e) {
      console.error('resolveDepartmentIdFromScopeNames', e);
      return null;
    }
  };

  // جلب قسم الطالب من organizationalAccess (نفس هيكل الطالب: معرفات أو أسماء)
  useEffect(() => {
    authService.getCurrentUser().then((me) => {
      const access = me?.organizationalAccess?.[0];
      if (!access) return;
      const scope = normalizeOrgScope(access);
      if (scope?.departmentId) {
        setStudentDepartmentId(scope.departmentId);
        return;
      }
      const scopeNames = normalizeOrgScopeNames(access);
      if (scopeNames && (scopeNames.departmentName || scopeNames.institutionName || scopeNames.countryName)) {
        resolveDepartmentIdFromScopeNames(scopeNames).then((id) => {
          if (id) setStudentDepartmentId(id);
        });
      }
    }).catch(() => {});
  }, [currentLanguage]);

  // جلب ترتيب الطالب داخل قسمه (الخادم يحدد القسم من المستخدم؛ فلتر التاريخين اختياري)
  const fetchDepartmentLeaderboard = async (exerciseId, dateFrom, dateTo) => {
    if (!exerciseId) return;
    setLoadingDepartmentLeaderboard(true);
    try {
      const params = {};
      if (dateFrom) params.dateFrom = dateFrom;
      if (dateTo) params.dateTo = dateTo;
      const response = await api.get(`/exercises/${exerciseId}/leaderboard`, { params });
      if (response.data.success && response.data.data?.leaderboard) {
        setDepartmentLeaderboard(response.data.data.leaderboard);
      } else {
        setDepartmentLeaderboard([]);
      }
    } catch (error) {
      console.error('Error fetching department leaderboard:', error);
      setDepartmentLeaderboard([]);
    } finally {
      setLoadingDepartmentLeaderboard(false);
    }
  };

  const applyRankingDateFilter = () => {
    setAppliedRankingDateFrom(rankingDateFrom);
    setAppliedRankingDateTo(rankingDateTo);
  };

  const clearRankingFilter = () => {
    setRankingDateFrom('');
    setRankingDateTo('');
    setAppliedRankingDateFrom('');
    setAppliedRankingDateTo('');
  };

  // عند التبديل لتاب ترتيب الطلاب وجاهزية التمرين، جلب الترتيب (القسم يُحدد من الخادم)
  useEffect(() => {
    if (activeTab !== 'departmentRanking') return;
    const exerciseId = availableExercise?.exerciseId || availableExercise?.id;
    if (exerciseId) {
      fetchDepartmentLeaderboard(exerciseId, appliedRankingDateFrom, appliedRankingDateTo);
    }
  }, [activeTab, availableExercise, appliedRankingDateFrom, appliedRankingDateTo]);

  const formatAvgTime = (ms) => {
    if (ms == null || ms === '') return '-';
    const sec = Math.round(Number(ms) / 1000);
    if (sec < 60) return `${sec} ${t('dailyExercises.seconds')}`;
    const m = Math.floor(sec / 60);
    const s = sec % 60;
    return `${m}:${String(s).padStart(2, '0')} ${t('dailyExercises.minutes')}`;
  };

  const getMedalStyle = (rank) => {
    if (rank === 1) return { icon: 'text-amber-500', row: 'bg-amber-50/70 border-l-4 border-amber-400' };
    if (rank === 2) return { icon: 'text-slate-400', row: 'bg-slate-50/80 border-l-4 border-slate-400' };
    if (rank === 3) return { icon: 'text-amber-700', row: 'bg-amber-100/50 border-l-4 border-amber-600' };
    return { icon: '', row: '' };
  };

  // جلب إحصائيات التمرين
  const fetchExerciseStats = async (exerciseId) => {
    if (!exerciseId) return;

    setLoadingStats(true);
    try {
      // الحصول على تواريخ السنة الحالية
      const now = new Date();
      const yearStart = new Date(now.getFullYear(), 0, 1);
      const yearEnd = new Date(now.getFullYear(), 11, 31);
      
      const dateFrom = yearStart.toISOString().split('T')[0];
      const dateTo = yearEnd.toISOString().split('T')[0];

      const response = await api.get(`/exercises/${exerciseId}/stats`, {
        params: {
          dateFrom,
          dateTo
        }
      });

      if (response.data.success) {
        setExerciseStats(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching exercise stats:', error);
      // لا نعرض toast لأن الإحصائيات ليست ضرورية
    } finally {
      setLoadingStats(false);
    }
  };

  // إعادة تعيين التمرين
  const resetExercise = () => {
    // تنظيف التوقيتات
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
    if (roundTimeoutRef.current) {
      clearTimeout(roundTimeoutRef.current);
    }
    trafficLightTimeoutsRef.current.forEach((t) => clearTimeout(t));
    trafficLightTimeoutsRef.current = [];
    if (breakCountdownIntervalRef.current) {
      clearInterval(breakCountdownIntervalRef.current);
      breakCountdownIntervalRef.current = null;
    }

    // إعادة تعيين الحالة (بما فيها الاستراحة)
    setBreakCountdown(null);
    setBreakPaused(false);
    setTrafficLightIndex(0);
    pendingNextRoundRef.current = null;
    breakPausedRef.current = false;
    setGameState('checking');
    setExerciseData(null);
    setAttemptId(null);
    setCurrentRound(0);
    setCurrentRoundData(null);
    setCurrentNumberIndex(0);
    setStudentAnswer('');
    setRoundStartTime(null);
    setRoundAnswers([]);
    setFinalResult(null);
    setExerciseStats(null);

    // التحقق من تمرين جديد
    checkAvailableExercise();
  };

  // تنظيف عند إلغاء التحميل
  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      if (roundTimeoutRef.current) {
        clearTimeout(roundTimeoutRef.current);
      }
      trafficLightTimeoutsRef.current.forEach((t) => clearTimeout(t));
      trafficLightTimeoutsRef.current = [];
      const ctx = audioContextRef.current;
      if (ctx && ctx.state !== 'closed') {
        void ctx.close();
      }
      audioContextRef.current = null;
    };
  }, []);

  // التحقق من التمرين المتاح عند تحميل المكون
  useEffect(() => {
    checkAvailableExercise();
  }, []);

  // شاشة التحقق من التمرين
  if (gameState === 'checking') {
    return (
      <div
        className="relative mx-auto w-full max-w-6xl space-y-5 px-3 pb-8 sm:space-y-6 sm:px-4 sm:pb-10"
        dir={isRTL ? 'rtl' : 'ltr'}
      >
        <DailyExercisesHero
          title={t('dailyExercises.title')}
          description={t('dailyExercises.pageDescription')}
          icon={CalendarIcon}
          iconClassName="text-sky-600"
        />
        <div className="flex min-h-[min(50vh,28rem)] items-center justify-center">
          <div className="w-full max-w-md rounded-[1.35rem] border-[3px] border-sky-200/80 bg-gradient-to-br from-white to-sky-50/80 p-8 text-center shadow-[0_8px_0_0_rgba(14,165,233,0.1)]">
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-2xl border-[3px] border-sky-200 bg-sky-50">
              <div className="h-9 w-9 animate-spin rounded-full border-[3px] border-sky-200 border-t-sky-600" aria-hidden />
            </div>
            <p className="text-lg font-black text-slate-900">{t('dailyExercises.checkingExercise')}</p>
            <p className="mt-2 text-sm font-semibold text-slate-500">{t('dailyExercises.checkingHint')}</p>
          </div>
        </div>
      </div>
    );
  }

  // شاشة عدم وجود تمرين
  if (gameState === 'noExercise') {
    return (
      <div
        className="relative mx-auto w-full max-w-6xl space-y-5 px-3 pb-8 sm:space-y-6 sm:px-4 sm:pb-10"
        dir={isRTL ? 'rtl' : 'ltr'}
      >
        <DailyExercisesHero
          title={t('dailyExercises.title')}
          description={t('dailyExercises.pageDescription')}
          icon={CalendarIcon}
          iconClassName="text-sky-600"
        />
        <div className="rounded-[1.35rem] border-[3px] border-slate-200/90 bg-gradient-to-br from-white to-sky-50/60 p-6 text-center shadow-[0_8px_0_0_rgba(14,165,233,0.08)] sm:p-8">
          <div className="mx-auto mb-4 flex h-20 w-20 items-center justify-center rounded-[1.25rem] border-[3px] border-sky-200 bg-sky-50">
            <CalendarIcon className="h-11 w-11 text-sky-500" strokeWidth={2} />
          </div>
          <h2 className="text-xl font-black text-slate-900 sm:text-2xl">{t('dailyExercises.noExerciseAvailable')}</h2>
          <p className="mt-2 mb-6 text-sm font-semibold leading-relaxed text-slate-600 sm:text-base">{t('dailyExercises.noExerciseDescription')}</p>
          <button
            type="button"
            onClick={checkAvailableExercise}
            disabled={loadingExercise}
            className="inline-flex min-h-[3rem] items-center justify-center gap-2 rounded-2xl border-[3px] border-emerald-700/40 bg-gradient-to-b from-emerald-400 to-emerald-500 px-8 py-3 text-base font-black text-white shadow-[0_3px_0_0_rgba(6,95,70,0.45)] transition hover:brightness-105 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {loadingExercise ? t('common.loading') : t('dailyExercises.checkAgain')}
          </button>
        </div>
        <DailyExercisesStatsRankingSection
          t={t}
          formatNumber={formatNumber}
          formatDate={formatDate}
          formatAvgTime={formatAvgTime}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          exerciseStats={exerciseStats}
          loadingStats={loadingStats}
          availableExercise={availableExercise}
          departmentLeaderboard={departmentLeaderboard}
          loadingDepartmentLeaderboard={loadingDepartmentLeaderboard}
          showRankingFilter={showRankingFilter}
          setShowRankingFilter={setShowRankingFilter}
          rankingDateFrom={rankingDateFrom}
          rankingDateTo={rankingDateTo}
          setRankingDateFrom={setRankingDateFrom}
          setRankingDateTo={setRankingDateTo}
          appliedRankingDateFrom={appliedRankingDateFrom}
          appliedRankingDateTo={appliedRankingDateTo}
          applyRankingDateFilter={applyRankingDateFilter}
          clearRankingFilter={clearRankingFilter}
          getMedalStyle={getMedalStyle}
        />
      </div>
    );
  }

  // شاشة الاستعداد
  if (gameState === 'ready') {
    return (
      <div
        className="relative mx-auto w-full max-w-6xl space-y-5 px-3 pb-8 sm:space-y-6 sm:px-4 sm:pb-10"
        dir={isRTL ? 'rtl' : 'ltr'}
      >
        <DailyExercisesHero
          title={t('dailyExercises.title')}
          description={t('dailyExercises.pageDescription')}
          icon={CalendarIcon}
          iconClassName="text-sky-600"
        />
        {availableExercise && (
          <div className="rounded-[1.35rem] border-[3px] border-violet-200/90 bg-gradient-to-br from-white via-violet-50/40 to-amber-50/50 p-6 shadow-[0_8px_0_0_rgba(139,92,246,0.1)] sm:p-8">
            <div className="mb-6 flex flex-wrap justify-center gap-3 text-center">
              <span className="inline-flex items-center rounded-xl border-2 border-sky-200 bg-white px-4 py-2 text-sm font-bold text-slate-800 shadow-sm">
                {t('dailyExercises.totalRounds')}: {formatNumber(availableExercise.totalRounds)}
              </span>
              <span className="inline-flex items-center rounded-xl border-2 border-amber-200 bg-white px-4 py-2 text-sm font-bold text-slate-800 shadow-sm">
                {t('dailyExercises.maxAttemptsPerDay')}: {formatNumber(availableExercise.maxAttemptsPerDay)}
              </span>
            </div>
            <div className="flex justify-center">
              <button
                type="button"
                onClick={startExercise}
                disabled={isLoading}
                className="inline-flex min-h-[3.25rem] items-center gap-3 rounded-2xl border-[3px] border-sky-800/25 bg-gradient-to-b from-sky-400 to-sky-600 px-10 py-4 text-lg font-black text-white shadow-[0_4px_0_0_rgba(15,23,42,0.25)] transition hover:brightness-105 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <PlayIcon className="h-7 w-7 shrink-0" strokeWidth={2.5} />
                {isLoading ? t('dailyExercises.starting') : t('dailyExercises.startTraining')}
              </button>
            </div>
          </div>
        )}
        <DailyExercisesStatsRankingSection
          t={t}
          formatNumber={formatNumber}
          formatDate={formatDate}
          formatAvgTime={formatAvgTime}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          exerciseStats={exerciseStats}
          loadingStats={loadingStats}
          availableExercise={availableExercise}
          departmentLeaderboard={departmentLeaderboard}
          loadingDepartmentLeaderboard={loadingDepartmentLeaderboard}
          showRankingFilter={showRankingFilter}
          setShowRankingFilter={setShowRankingFilter}
          rankingDateFrom={rankingDateFrom}
          rankingDateTo={rankingDateTo}
          setRankingDateFrom={setRankingDateFrom}
          setRankingDateTo={setRankingDateTo}
          appliedRankingDateFrom={appliedRankingDateFrom}
          appliedRankingDateTo={appliedRankingDateTo}
          applyRankingDateFilter={applyRankingDateFilter}
          clearRankingFilter={clearRankingFilter}
          getMedalStyle={getMedalStyle}
        />
      </div>
    );
  }

  // شاشة إشارة المرور - إعداد الطفل قبل أول رقم (مصباح واحد مضيء في كل لحظة)
  if (gameState === 'countdown' && currentRoundData) {
    const lights = [
      { color: 'bg-yellow-500', shadow: 'shadow-yellow-500/50' },
      { color: 'bg-orange-500', shadow: 'shadow-orange-500/50' },
      { color: 'bg-green-500', shadow: 'shadow-green-500/50' }
    ];
    return (
      <div className="fixed inset-0 z-[100] flex h-[100dvh] max-h-[100dvh] items-center justify-center overflow-y-auto bg-black px-4 py-4 sm:overflow-hidden">
        <div className="flex flex-col items-center gap-8">
          <p className="text-gray-400 text-lg mb-4">{t('dailyExercises.getReady')}</p>
          <div className="flex gap-8">
            {lights.map((light, i) => (
              <div
                key={i}
                className={`w-20 h-20 rounded-full border-4 border-gray-700 transition-all duration-500 ${
                  trafficLightIndex === i + 1
                    ? `${light.color} ${light.shadow} shadow-2xl scale-110`
                    : 'bg-gray-800'
                }`}
              />
            ))}
          </div>
        </div>
      </div>
    );
  }

  // شاشة اللعب - عرض الأرقام
  if (gameState === 'playing' && currentRoundData) {
    const flashAnzanSession = currentRoundData.flashAnzanSession;
    const currentNumber = flashAnzanSession?.numbers?.[currentNumberIndex] || '';

    return (
      <div className="fixed inset-0 z-[100] flex h-[100dvh] max-h-[100dvh] flex-col overflow-hidden bg-black px-3 py-2">
        <div className="flex min-h-0 flex-1 flex-col items-center justify-center overflow-hidden px-2">
          <div className="mx-auto flex min-h-0 w-full max-w-[min(100vw,56rem)] flex-1 flex-col items-center justify-center text-center">
            <FullscreenNumberDisplay value={currentNumber} tickKey={currentNumberIndex} />
            <div className="mt-4 shrink-0 text-base text-gray-400 sm:text-lg">
              {formatNumber(currentNumberIndex + 1)} / {formatNumber(flashAnzanSession?.numbers?.length || 0)}
            </div>
            <div className="mt-1 shrink-0 text-sm text-gray-500">
              {t('dailyExercises.round')} {formatNumber(currentRound + 1)} {t('dailyExercises.of')}{' '}
              {formatNumber(exerciseData?.rounds?.length || 0)}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // لوحة مفاتيح الأرقام
  const handleKeypadInput = (key) => {
    if (key === 'backspace') {
      setStudentAnswer((prev) => prev.slice(0, -1));
      return;
    }
    if (key === '-') {
      if (studentAnswer !== '') return;
      setStudentAnswer('-');
      return;
    }
    const newVal = studentAnswer + key;
    if (/^-?\d*$/.test(newVal)) setStudentAnswer(newVal);
  };

  // شاشة إدخال الإجابة
  if (gameState === 'answer' && currentRoundData) {
    const keypadRows = [
      ['7', '8', '9'],
      ['4', '5', '6'],
      ['1', '2', '3'],
      ['-', '0', '⌫']
    ];
    return (
      <div className="fixed inset-0 z-[100] flex h-[100dvh] max-h-[100dvh] items-center justify-center overflow-y-auto overscroll-contain bg-black px-4 py-3">
        <div className="text-center max-w-md w-full px-6 py-6">
          <div className="mb-6">
            <CheckCircleIcon className="h-16 w-16 text-green-400 mx-auto mb-4" />
            <h2 className="text-3xl font-bold text-white mb-4">
              {t('dailyExercises.enterAnswer')}
            </h2>
            <p className="text-gray-300 mb-6">
              {t('dailyExercises.calculateResult')}
            </p>
          </div>
          
          <div className="mb-6">
            <input
              type="text"
              inputMode="numeric"
              pattern="[0-9\-]*"
              value={studentAnswer}
              onChange={(e) => {
                const value = e.target.value;
                if (value === '' || /^-?\d*$/.test(value)) {
                  setStudentAnswer(value);
                }
              }}
              placeholder={t('dailyExercises.yourAnswer')}
              className="w-full px-6 py-4 text-2xl text-center bg-gray-800 text-white border-2 border-gray-600 rounded-lg focus:border-yellow-500 focus:outline-none"
              autoFocus
              onKeyPress={(e) => {
                if (e.key === 'Enter') submitRoundAnswer();
              }}
            />
          </div>

          {/* لوحة مفاتيح الأرقام */}
          <div className="grid grid-cols-3 gap-2 mb-6">
            {keypadRows.map((row, rowIdx) =>
              row.map((key) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => (key === '⌫' ? handleKeypadInput('backspace') : handleKeypadInput(key))}
                  className="py-4 text-xl font-bold bg-gray-700 hover:bg-gray-600 active:bg-gray-500 text-white rounded-lg transition-colors select-none"
                >
                  {key}
                </button>
              ))
            )}
          </div>
          
          <button
            onClick={submitRoundAnswer}
            disabled={isLoading || !studentAnswer}
            className="w-full px-8 py-4 bg-green-500 hover:bg-green-600 text-white font-bold text-xl rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? t('dailyExercises.submitting') : t('dailyExercises.submitAnswer')}
          </button>
        </div>
      </div>
    );
  }

  // شاشة الاستراحة بعد كل 10 جولات: عد تنازلي 6 ثوانٍ + زر خذ استراحة / استكمال
  if (gameState === 'break') {
    const handleTakeBreak = () => {
      if (breakCountdownIntervalRef.current) {
        clearInterval(breakCountdownIntervalRef.current);
        breakCountdownIntervalRef.current = null;
      }
      breakPausedRef.current = true;
      setBreakPaused(true);
    };
    return (
      <div className="fixed inset-0 z-[100] flex h-[100dvh] max-h-[100dvh] items-center justify-center overflow-y-auto bg-black px-4 py-4 sm:overflow-hidden">
        <div className="text-center max-w-2xl w-full px-6">
          <div className="mb-8">
            <div className="text-8xl mb-4">☕</div>
            <h2 className="text-4xl font-bold text-white mb-4">
              {t('dailyExercises.breakAfter10Rounds')}
            </h2>
            {!breakPaused ? (
              <>
                <p className="text-xl text-gray-300 mb-4">
                  {t('dailyExercises.nextRoundInCountdown')}
                </p>
                <p className="text-5xl font-bold text-primary-400 mb-6 tabular-nums">
                  {breakCountdown != null ? breakCountdown : 6}
                </p>
                <button
                  type="button"
                  onClick={handleTakeBreak}
                  className="px-6 py-3 bg-amber-500 hover:bg-amber-600 text-white font-semibold rounded-lg transition-colors"
                >
                  {t('dailyExercises.takeBreak')}
                </button>
              </>
            ) : (
              <>
                <p className="text-xl text-gray-300 mb-6">
                  {t('dailyExercises.breakPausedMessage')}
                </p>
                <button
                  type="button"
                  onClick={startNextRoundAfterBreak}
                  className="px-6 py-3 bg-primary-500 hover:bg-primary-600 text-white font-semibold rounded-lg transition-colors"
                >
                  {t('dailyExercises.continueOrResume')}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  // شاشة انتهاء الجولة (لم تعد مستخدمة بين الجولات؛ الجولة التالية تبدأ مباشرة أو بعد الاستراحة)
  if (gameState === 'roundComplete') {
    return (
      <div className="fixed inset-0 z-[100] flex h-[100dvh] max-h-[100dvh] items-center justify-center overflow-y-auto bg-black px-4 py-4 sm:overflow-hidden">
        <div className="text-center max-w-2xl w-full px-6">
          <div className="mb-8">
            <div className="text-8xl mb-4">⏳</div>
            <h2 className="text-4xl font-bold text-white mb-4">
              {t('dailyExercises.roundCompleted')}
            </h2>
            <p className="text-xl text-gray-300 mb-6">
              {t('dailyExercises.nextRoundStarting')}
            </p>
            <div className="text-2xl text-white">
              {t('dailyExercises.round')} {formatNumber(currentRound + 1)} {t('dailyExercises.of')} {formatNumber(exerciseData?.rounds?.length || 0)}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // شاشة النتيجة النهائية
  if (gameState === 'finalResult' && finalResult) {
    const totalScore = finalResult.totalScore;
    const totalRounds = finalResult.rounds.length;
    const correctRounds = finalResult.rounds.filter(r => r.isCorrect).length;
    const accuracy = (correctRounds / totalRounds) * 100;

    // تحديد الرسالة والصوت حسب النتيجة
    let message = '';
    let emoji = '';
    let messageColor = '';

    if (accuracy >= 90) {
      message = t('dailyExercises.excellentResult');
      emoji = '🎉🎉🎉';
      messageColor = 'text-green-400';
    } else if (accuracy >= 70) {
      message = t('dailyExercises.goodResult');
      emoji = '🎉🎉';
      messageColor = 'text-green-300';
    } else if (accuracy >= 50) {
      message = t('dailyExercises.fairResult');
      emoji = '👍';
      messageColor = 'text-yellow-400';
    } else {
      message = t('dailyExercises.keepPracticing');
      emoji = '💪';
      messageColor = 'text-orange-400';
    }

    return (
      <div className="fixed inset-0 z-[100] max-h-[100dvh] overflow-y-auto bg-black">
        <div className="flex min-h-full flex-col items-center px-3 pb-16 pt-6 md:pb-24 md:pt-8">
          <div className="text-center max-w-2xl w-full px-6">
          <div className="mb-8">
            <div className="text-8xl mb-4">{emoji}</div>
            
            <h2 className={`text-4xl font-bold mb-4 ${messageColor}`}>
              {message}
            </h2>
            
            <div className="bg-gray-800 rounded-lg p-6 mb-6">
              <div className="grid grid-cols-2 gap-4 text-white mb-4">
                <div>
                  <p className="text-gray-400 text-sm mb-1">{t('dailyExercises.totalScore')}</p>
                  <p className="text-3xl font-bold text-primary-400">{formatNumber(totalScore)}</p>
                </div>
                <div>
                  <p className="text-gray-400 text-sm mb-1">{t('dailyExercises.totalTime')}</p>
                  <p className="text-3xl font-bold text-primary-400">{formatNumber(finalResult.totalTimeMs / 1000, 1)}s</p>
                </div>
                <div>
                  <p className="text-gray-400 text-sm mb-1">{t('dailyExercises.correctRounds')}</p>
                  <p className="text-3xl font-bold text-green-400">{formatNumber(correctRounds)} / {formatNumber(totalRounds)}</p>
                </div>
                <div>
                  <p className="text-gray-400 text-sm mb-1">{t('dailyExercises.accuracy')}</p>
                  <p className="text-3xl font-bold text-yellow-400">{formatNumber(accuracy, 1)}%</p>
                </div>
              </div>

              <div className="mt-4 pt-4 border-t border-gray-700">
                <h3 className="text-lg font-semibold text-white mb-3">
                  {t('dailyExercises.roundDetails')}
                </h3>
                <div className="space-y-2 text-sm">
                  {finalResult.rounds.map((round, index) => (
                    <div key={index} className="flex items-center justify-between text-gray-300">
                      <span>
                        {t('dailyExercises.round')} {formatNumber(round.roundNumber)}: {formatNumber(round.studentAnswer)}
                      </span>
                      <div className="flex items-center gap-2">
                        {round.isCorrect ? (
                          <>
                            <CheckCircleIcon className="w-4 h-4 text-green-400" />
                            <span className="text-green-400">+{formatNumber(round.score)}</span>
                          </>
                        ) : (
                          <>
                            <XCircleIcon className="w-4 h-4 text-red-400" />
                            <span className="text-red-400">0</span>
                            <span className="text-gray-500 text-xs">({t('dailyExercises.correct')}: {formatNumber(round.correctAnswer)})</span>
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <button
              onClick={resetExercise}
              className="flex items-center gap-2 px-6 py-3 bg-primary-500 hover:bg-primary-600 text-white font-semibold rounded-lg transition-colors mx-auto"
            >
              <ArrowLeftIcon className="w-5 h-5" />
              {t('dailyExercises.backToStart')}
            </button>
          </div>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default DailyExercises;
